import { useEffect, useState } from "react";

import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import useFuseSearch from "@/hooks/useFuseSearch";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import MultiSelectModalConfig from "@/ui/composed/modal/MultiSelectModal/MultiSelectModalConfig";
import MultiSelectModalDismissConfirmationAlert from "@/ui/composed/modal/MultiSelectModal/MultiSelectModalDismissConfirmationAlert";
import MultiSelectModalSearchTextField from "@/ui/composed/modal/MultiSelectModal/MultiSelectModalSearchTextField";
import { FAIconName, FloatingButton } from "@/ui/core";
import SelectableCard from "@/ui/core/SelectableCard";
import { FlatList, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

interface MultiSelectModalProps extends ViewWithTestID {
  defaultSelectedValues?: MultiSelectOption[];
  isOpen: boolean;
  isSearchable?: boolean;
  isSingleSelect?: boolean;
  isSmallBottomPadding?: boolean;
  onDismiss: () => void;
  onValuesChange: (values: MultiSelectOption[]) => void;
  options: MultiSelectOption[];
  shouldAllowEmptySelection?: boolean;
  title: string;
}

const MultiSelectModal = ({
  defaultSelectedValues,
  isOpen,
  isSearchable,
  isSingleSelect,
  isSmallBottomPadding,
  onDismiss,
  onValuesChange,
  options,
  shouldAllowEmptySelection = false,
  testID,
  title,
}: MultiSelectModalProps) => {
  const baseTestID = joinTestIDs("multi-select", "modal", testID);
  // the FAB is absolutely positioned inside the sheet, so on Android its fixed bottom
  // offset lands behind the gesture/nav bar; add the bottom inset to lift it clear
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [isDiscardAlertOpen, setIsDiscardAlertOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedValues, setSelectedValues] = useState<MultiSelectOption[]>([]);

  useEffect(() => {
    if (isNil(defaultSelectedValues)) {
      return;
    }
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  const values = useFuseSearch<MultiSelectOption>({
    data: options,
    searchKeys: ["displayText"],
    searchValue,
    threshold: 0.4,
  });

  const hasUnsavedChanges = !isEqual(
    selectedValues,
    defaultSelectedValues || [],
  );
  const isValidSelection =
    selectedValues.length > 0 || shouldAllowEmptySelection;
  const shouldShowFloatingButton = hasUnsavedChanges && isValidSelection;

  const handleSelectOption = (option: MultiSelectOption) => {
    if (isSingleSelect) {
      setSelectedValues([option]);
      return;
    }

    setSelectedValues((prevOptions) => {
      if (prevOptions.some((prevOption) => prevOption.value === option.value)) {
        return prevOptions.filter(
          (prevOption) => prevOption.value !== option.value,
        );
      }

      return [...prevOptions, option];
    });
  };

  const handleRequestDismiss = () => {
    if (hasUnsavedChanges) {
      setIsDiscardAlertOpen(true);
      return;
    }

    onDismiss();
  };

  const handleConfirmDismiss = () => {
    setIsDiscardAlertOpen(false);
    setSelectedValues(defaultSelectedValues || []);
    onDismiss();
  };

  const handleConfirm = () => {
    onValuesChange(selectedValues);
    onDismiss();
  };

  const handleModalDismissed = () => {
    setSelectedValues(defaultSelectedValues || []);
  };

  return (
    <BottomSheetModal
      contentContainerGap={8}
      dismissAccessibilityLabel={
        MultiSelectModalConfig.DismissButtonAccessibilityLabel
      }
      FABComponent={
        <FloatingButton
          leftIcon={FAIconName.Check}
          onPress={handleConfirm}
          paddingBottom={bottomInset}
          testID={joinTestIDs(baseTestID, "confirm")}
          title={MultiSelectModalConfig.ConfirmButtonTitle}
        />
      }
      isOpen={isOpen}
      isScrollable
      isSmallBottomPadding={isSmallBottomPadding}
      onDismissAnimationEnd={handleModalDismissed}
      onRequestClose={handleRequestDismiss}
      shouldRenderFloatingButton={shouldShowFloatingButton}
      testID={baseTestID}
      title={title}
    >
      <VStack flex={1} gap={12}>
        <MultiSelectModalSearchTextField
          isSearchable={isSearchable}
          onChange={(text) => setSearchValue(text)}
          testID={baseTestID}
          value={searchValue}
        />
        <FlatList
          data={values}
          isBounceEnabled={false}
          isFABVisible
          isInsideBottomSheet
          renderItem={({ item }) => (
            <SelectableCard
              isSelected={selectedValues.some((e) => e.value === item.value)}
              label={item.displayText}
              onSelect={() => handleSelectOption(item)}
              testID={joinTestIDs(baseTestID, item.value)}
            />
          )}
          shouldRemoveStyleHorizontalPadding
          testID={baseTestID}
        />
      </VStack>

      <MultiSelectModalDismissConfirmationAlert
        isOpen={isDiscardAlertOpen}
        onConfirm={handleConfirmDismiss}
        onDismiss={() => setIsDiscardAlertOpen(false)}
        testID={baseTestID}
      />
    </BottomSheetModal>
  );
};

export type { MultiSelectModalProps };
export default MultiSelectModal;
